import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatCurrency } from "@/lib/api";
import { AlertTriangle, AlertCircle, Info, CheckCircle, RefreshCw, Brain, Wrench } from "lucide-react";

export default function AnomaliesPage() {
  const { activeEntity, entities } = useApp();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState(activeEntity?.id || "");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const canFix = ["system_admin", "asset_manager"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entityFilter) params.entity_id = entityFilter;
      const { data: d } = await api.get("/anomalies", { params });
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [entityFilter]);
  useEffect(() => { if (activeEntity) setEntityFilter(activeEntity.id); }, [activeEntity]);

  const aiAssess = async () => {
    setAiLoading(true);
    try {
      const { data: d } = await api.post("/anomalies/ai-assess", { entity_id: entityFilter || undefined });
      setAiResult(d);
    } catch (e) { console.error(e); }
    finally { setAiLoading(false); }
  };

  const autoFix = async () => {
    if (!aiResult?.auto_fixes?.length) return;
    if (!window.confirm(`Apply ${aiResult.auto_fixes.length} automatic fixes?\n\nThis will:\n- Stop depreciation on over-depreciated assets\n- Recalculate carrying amounts\n\nThis action is audit-logged.`)) return;
    setFixing(true);
    try {
      const { data: d } = await api.post("/anomalies/auto-fix", { fixes: aiResult.auto_fixes });
      alert(`${d.fixed} fixes applied. Reloading...`);
      setAiResult(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setFixing(false); }
  };

  const sevIcon = (s) => {
    if (s === "critical") return <AlertCircle size={16} className="text-[#991B1B]" />;
    if (s === "warning") return <AlertTriangle size={16} className="text-[#92400E]" />;
    return <Info size={16} className="text-[#1E40AF]" />;
  };
  const sevBg = (s) => {
    if (s === "critical") return "bg-[#991B1B]/5 border-l-4 border-l-[#991B1B]";
    if (s === "warning") return "bg-[#92400E]/5 border-l-4 border-l-[#92400E]";
    return "bg-[#1E40AF]/5 border-l-4 border-l-[#1E40AF]";
  };

  return (
    <div className="space-y-6" data-testid="anomalies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <AlertTriangle size={24} className="inline mr-2 text-[#C8A961]" />
            Anomaly Detection
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Automated checks + AI assessment for errors, spikes, and inconsistencies</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} data-testid="anomaly-entity-filter">
            <option value="">All Entities</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={load} className="px-3 py-2 rounded-lg border border-[#E8E2D5] hover:bg-[#FAF8F4] flex items-center gap-2 text-sm" data-testid="rescan-btn">
            <RefreshCw size={14} /> Re-scan
          </button>
          {canFix && data && data.total > 0 && (
            <button onClick={aiAssess} disabled={aiLoading} className="stratum-btn-primary flex items-center gap-2" data-testid="ai-assess-btn">
              <Brain size={14} /> {aiLoading ? "Analysing..." : "AI Assess & Recommend"}
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stratum-card p-5">
            <span className="text-xs text-[#6B7280] uppercase tracking-wider">Total Issues</span>
            <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums mt-2">{data.total}</div>
          </div>
          <div className="stratum-card p-5 border-l-4 border-l-[#991B1B]">
            <span className="text-xs text-[#991B1B] uppercase tracking-wider">Critical</span>
            <div className="font-mono text-2xl font-semibold text-[#991B1B] tabular-nums mt-2">{data.critical}</div>
          </div>
          <div className="stratum-card p-5 border-l-4 border-l-[#92400E]">
            <span className="text-xs text-[#92400E] uppercase tracking-wider">Warnings</span>
            <div className="font-mono text-2xl font-semibold text-[#92400E] tabular-nums mt-2">{data.warnings}</div>
          </div>
          <div className="stratum-card p-5 border-l-4 border-l-[#1E40AF]">
            <span className="text-xs text-[#1E40AF] uppercase tracking-wider">Info</span>
            <div className="font-mono text-2xl font-semibold text-[#1E40AF] tabular-nums mt-2">{data.info}</div>
          </div>
        </div>
      )}

      {/* AI Assessment Result */}
      {aiResult && (
        <div className="stratum-card p-6 border-l-4 border-l-[#C8A961]">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={18} className="text-[#C8A961]" />
            <h3 className="font-display text-base font-semibold text-[#0B1020]">AI Assessment</h3>
          </div>
          <p className="text-sm text-[#1A1A1A] mb-4">{aiResult.assessment}</p>
          
          {aiResult.auto_fixes?.length > 0 && canFix && (
            <div className="mb-4 p-4 rounded-lg bg-[#065F46]/5 border border-[#065F46]/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#065F46]">{aiResult.auto_fixes.length} automatic fixes available</span>
                <button onClick={autoFix} disabled={fixing} className="px-4 py-2 rounded-lg bg-[#065F46] text-white text-sm font-medium hover:bg-[#064E3B] flex items-center gap-2" data-testid="apply-fixes-btn">
                  <Wrench size={14} /> {fixing ? "Applying..." : "Apply All Fixes"}
                </button>
              </div>
              {aiResult.auto_fixes.map((f, i) => (
                <div key={i} className="text-xs text-[#065F46] py-1">
                  <span className="font-mono font-medium">{f.tag}</span>: {f.reason}
                </div>
              ))}
            </div>
          )}
          
          {aiResult.recommendations?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-2">Recommendations</h4>
              {aiResult.recommendations.slice(0, 10).map((r, i) => (
                <div key={i} className="flex items-start gap-2 py-2 border-b border-[#E8E2D5] last:border-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.priority === "high" ? "bg-[#991B1B]/10 text-[#991B1B]" : r.priority === "medium" ? "bg-[#92400E]/10 text-[#92400E]" : "bg-[#1E40AF]/10 text-[#1E40AF]"}`}>{r.priority}</span>
                  <div>
                    <span className="font-mono text-xs font-medium">{r.asset_tag}</span>
                    <span className="text-xs text-[#6B7280] ml-2">{r.action}</span>
                    {r.reason && <p className="text-[10px] text-[#6B7280] mt-0.5">{r.reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Anomaly List */}
      {loading ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Scanning...</div>
      ) : !data || data.total === 0 ? (
        <div className="stratum-card p-12 text-center">
          <CheckCircle size={40} className="mx-auto text-[#065F46] mb-3" />
          <h3 className="font-display text-lg font-semibold text-[#065F46]">All Clear</h3>
          <p className="text-[#6B7280] mt-1">No anomalies detected.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.anomalies.map((a, i) => (
            <div key={i} className={`stratum-card p-4 ${sevBg(a.severity)}`} data-testid={`anomaly-${i}`}>
              <div className="flex items-start gap-3">
                {sevIcon(a.severity)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-medium text-[#0B1020]">{a.tag}</span>
                    <span className="text-xs text-[#6B7280]">{a.description}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${a.severity === "critical" ? "bg-[#991B1B]/10 text-[#991B1B]" : a.severity === "warning" ? "bg-[#92400E]/10 text-[#92400E]" : "bg-[#1E40AF]/10 text-[#1E40AF]"}`}>{a.type.replace(/_/g, " ")}</span>
                  </div>
                  <p className="text-sm text-[#1A1A1A]">{a.message}</p>
                  <p className="text-xs text-[#C8A961] mt-1 font-medium">Action: {a.action}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
